//@ts-nocheck
import { ChartOptions } from "chart.js";
import moment from "moment";
import {
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_STYLE } from "../../../constants/design/fontSize";
import { SPACING } from "../../../constants/design/spacing";
import { FUNDING_RATE_PRECISION } from "../../../constants/precision/form";
import { drawCircle } from "../../../utils/canvas";
import { cssColor } from "../../../utils/themeColor";

export const upColor = (a?: number) => cssColor("--color-up", "22 199 132", a);
export const downColor = (a?: number) => cssColor("--color-down", "234 57 67", a);

export function buildFundingChartOptions(
  scaleRanges: { minY: number; maxY: number },
  hoveredIndexRef: { current: number | undefined }
): ChartOptions<"line"> {
  return {
    maintainAspectRatio: false,
    responsive: true,
    animation: false,
    layout: { padding: { right: SPACING.two } },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      y: {
        display: true,
        position: "right",
        min: scaleRanges.minY,
        max: scaleRanges.maxY,
        ticks: {
          maxTicksLimit: 5,
          font: { family: "BaseFont", size: 11 },
          color: TEXT_COLORS.three,
          callback(value) {
            return `${(Number(value) * 100).toFixed(FUNDING_RATE_PRECISION)}%`;
          },
        },
        grid: {
          color: (ctx) =>
            ctx.tick.value === 0 ? BORDER_COLORS.two : "transparent",
        },
        border: { display: false },
      },
      x: {
        type: "category",
        display: true,
        ticks: {
          maxTicksLimit: 6,
          maxRotation: 0,
          font: { family: "BaseFont", size: 11 },
          color: TEXT_COLORS.three,
          callback(value) {
            const label = this.getLabelForValue(value as number);
            return moment.unix(Number(label)).format("DD MMM YY");
          },
        },
        grid: { color: "transparent" },
        border: { display: false },
      },
    },
    interaction: { intersect: false, mode: "index" },
    onHover: (_: any, elements: any, chart: any) => {
      const newIndex = elements?.length ? elements[0].index : undefined;
      if (hoveredIndexRef.current !== newIndex) {
        hoveredIndexRef.current = newIndex;
        // Redraw only — no data recalculation
        chart?.draw();
      }
    },
  };
}

export function buildFundingHoverPlugins(hoveredIndexRef: {
  current: number | undefined;
}) {
  return [
    {
      id: "fundingHover",
      afterDraw: (c: any) => {
        const idx = hoveredIndexRef.current;
        if (idx === undefined) return;

        const meta = c.getDatasetMeta(0);
        const point = meta?.data?.[idx];
        if (!point) return;

        const { x, y } = point;
        const { top, bottom, left, right } = c.chartArea;
        const ctx: CanvasRenderingContext2D = c.ctx;
        const time = c.data.labels?.[idx];
        const funding = c.data.datasets[0]?.data?.[idx] ?? null;

        // Crosshair
        ctx.save();
        ctx.setLineDash([4, 4]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = COLORS.neutral.four;
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
        ctx.stroke();
        ctx.restore();

        // Dot
        drawCircle(ctx, x, y, 3, COLORS.neutral.one, COLORS.neutral.one, 0);
        drawCircle(ctx, x, y, 5, COLORS.neutral.four, COLORS.neutral.four, 0);

        // Labels
        ctx.save();
        ctx.font = `${FONT_STYLE.data.three.fontSize} BaseFont`;
        ctx.textBaseline = "top";

        // Date label below x-axis
        ctx.fillStyle = TEXT_COLORS.one;
        ctx.textAlign = "center";
        ctx.fillText(
          moment.unix(Number(time)).format("DD MMM YY HH:mm"),
          x,
          bottom + 8
        );

        // Funding label right of y-axis
        if (funding !== null) {
          ctx.textAlign = "left";
          ctx.fillStyle =
            funding < 0 ? downColor() : upColor();
          ctx.fillText(
            `${funding >= 0 ? "+" : ""}${(funding * 100).toFixed(FUNDING_RATE_PRECISION)}%`,
            right + 8,
            y - 6
          );
        }
        ctx.restore();
      },
    },
  ];
}
